import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Paperclip } from "@phosphor-icons/react";
import AssetUploader from "@/components/AssetUploader";

/**
 * A collapsible panel that lets any authorised user (admin OR the job's owner
 * customer) upload additional assets to an existing job. Used at revision
 * stages so clients can drop new references as feedback loops progress.
 *
 * Props:
 *   jobId    — the job UUID
 *   onAdded  — called after successful upload so the parent can reload
 */
export default function AdditionalAssetsPanel({ jobId, onAdded, title = "Upload additional assets" }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!files.length) return toast.error("Drop some files first");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/assets`, { asset_ids: files.map((f) => f.id) });
      toast.success(`Added ${data.added} asset${data.added === 1 ? "" : "s"} to the project`);
      setFiles([]);
      setOpen(false);
      onAdded?.();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Upload failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-6 border border-[var(--gc-border)]" data-testid="additional-assets-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[var(--gc-cream,#fdf8f0)] hover:bg-[var(--gc-bg-alt)] font-semibold text-sm"
        data-testid="additional-assets-toggle"
      >
        <span className="inline-flex items-center gap-2">
          <Paperclip size={16} weight="bold" /> {title}
        </span>
        <span className="tech-label">{open ? "// close" : "// open"}</span>
      </button>
      {open && (
        <div className="p-4 space-y-3">
          <p className="text-xs text-[var(--gc-text-muted)]">
            Drop any extra references — mood boards, brand assets, updated brief docs. These are added to the project&apos;s asset list and posted as a note in the message thread so the team is notified.
          </p>
          <AssetUploader
            value={files}
            onChange={setFiles}
            label="Additional assets"
            testid="additional-assets-uploader"
          />
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={submit}
              disabled={submitting || files.length === 0}
              className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40"
              data-testid="additional-assets-submit"
            >
              <UploadSimple size={14} weight="bold" /> {submitting ? "Uploading…" : `Add ${files.length || ""} asset${files.length === 1 ? "" : "s"}`.trim()}
            </button>
            {files.length > 0 && (
              <button onClick={() => setFiles([])} className="btn-secondary px-4 py-2 text-sm">
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
